import { useState } from "react";
import { useLocalization } from "@fluent/react";
import { useFormData, useNotificationContext, ContextForm } from "cerulean";
import Address from "byzantine/src/Address";
import Organization from "byzantine/src/Organization";
import { Button, Row } from "@narmi/design_system";
import utils from "../../../../../utils";
import BankingUSAddressField from "../../../../address/BankingUSAddressField";
import useSudoApiHandler from "../../../../SudoContext/useSudoApiHandler";
import { useFingerPrintData } from "../../../../fingerprint";
import { checkAuthSession } from "../../../../../src/utils";

const BusinessAddressEditDialog = ({
  closeDialog,
  organization,
  setOrganization,
}: {
  closeDialog: () => void;
  organization?: Organization;
  setOrganization: (organization: Organization) => void;
}) => {
  const { l10n } = useLocalization();
  const { sendNotificationToParent } = useNotificationContext();
  const handleSudoRequest = useSudoApiHandler();
  const organizationAddress = { ...organization?.address };
  const { formData, setFormData, onChange } = useFormData({
    address: organizationAddress as API.Address,
  });
  const [addressErrors, setAddressErrors] = useState({});
  const { deviceId, deviceRequestId } = useFingerPrintData("update_address");

  const handleAddressChange = (update: Partial<API.Address>) => {
    setFormData((prev: { address: API.Address }) => ({
      ...prev,
      address: { ...prev.address, ...update },
    }));
  };

  const getAddressErrors = () => {
    const errors = utils.validateAddress(formData.address);
    setAddressErrors(errors);
    return errors;
  };

  const onSubmit = async (callback: CallableFunction) => {
    const formErrors = getAddressErrors();

    if (Object.keys(formErrors).length > 0) {
      return;
    }

    try {
      if (!organization) return;

      const address = new Address(formData.address);
      const response = await handleSudoRequest(() => {
        if (!organization) throw new Error("No organization");
        return organization.updateBusinessAddress(address, deviceId, deviceRequestId);
      });

      if (response && setOrganization) {
        organization.address = response;
        setOrganization(organization);
        sendNotificationToParent({
          type: "success",
          text: "Address updated.",
        });
        callback();
      }
      closeDialog();
    } catch (error) {
      if (error instanceof Error) {
        callback(error.message);
      } else {
        callback(error);
      }
      await checkAuthSession(l10n.getString("logout-message-blocked-and-locked"));
    }
  };

  return (
    <div className="margin--top--s">
      <ContextForm data={formData} onChange={onChange}>
        <ContextForm.Field field="address">
          <BankingUSAddressField
            onUpdate={handleAddressChange}
            streetAddressLabel={l10n.getString("label-business-street-address")}
            data={formData.address || {}}
            showTitle={false}
            errors={addressErrors}
            marginTop="none"
            marginBottom="margin--bottom--s"
          />
        </ContextForm.Field>

        <div className="margin--top--xl">
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                type="button"
                onClick={closeDialog}
                kind="negative"
                label={l10n.getString("button-cancel")}
              />
            </Row.Item>
            <Row.Item shrink>
              <ContextForm.Action onSubmit={onSubmit}>
                <Button kind="primary" label={l10n.getString("button-save-changes")} />
              </ContextForm.Action>
            </Row.Item>
          </Row>
        </div>
      </ContextForm>
    </div>
  );
};

export default BusinessAddressEditDialog;
